import { useAuth } from 'auth';
import AppStoreContext from 'contexts/AppStoreContext';
import CarnivoreContext from 'contexts/CarnivoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MarkupKitContext from 'contexts/MarkupKitContext';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import CarnivoreSheetEvents from 'editor/components/CarnivoreSheetEvents';
import Cursor from 'editor/components/Cursor';
import MoveMarkupInteraction from 'editor/components/interactions/MoveMarkupInteraction';
import Markup from 'editor/components/Markup';
import SheetEvents from 'editor/components/SheetEvents';
import { EditorTools } from 'editor/components/Tool';
import Immutable from 'immutable';
import noop from 'lodash/noop';
import { FC, useContext, useEffect } from 'react';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { PostRFI } from 'records/interactions/PostRFI';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { Markup as MarkupRecord } from 'records/Markup';
import { NewMarkup } from 'records/NewMarkup';
import { NewPunch } from 'records/NewPunch';
import { Space as SpaceRecord, SpaceId } from 'records/Space';
import { v4 as uuidv4 } from 'uuid';

type Props = {
  sheetComp: CarnivoreSheet;
  pageid: string;
};

const ECSSheetEvents: FC<Props> = (props: Props) => {
  const { sheetComp, pageid } = props;
  const {
    tool,
    mode,
    doc: carnDoc,
    carnCtx,
    domOverlay,
  } = useContext(ECSEditorContext);

  const world = useContext(ECSWorldContext);
  const carnLib = useContext(CarnivoreContext);
  const markupKit = useContext(MarkupKitContext);
  const appStore = useContext(AppStoreContext);
  const { actions } = appStore;
  const { userId } = useAuth();

  const ALLOWED_ROLES = ['admin', 'member'];
  const hasAllowedRole = () => {
    return ALLOWED_ROLES.includes(appStore.state.getUserRole(userId));
  };

  useEffect(() => {
    const carnSheetEventsEntity = world
      .createEntity()
      .add(carnDoc)
      .add(carnCtx)
      .add(carnLib)
      .add(domOverlay)
      .add(markupKit)
      .add(mode)
      .add(sheetComp)
      .add(tool)
      .add(new CarnivoreSheetEvents());

    // add the Cursor to the DOM layer (Using "old"? method for now)
    const cursorEntity = world.createEntity().add(domOverlay).add(new Cursor());

    const sheetEvents = new SheetEvents();

    sheetEvents.on('sheet:click', ({ hitMarkups, hitCps }) => {
      const postRFIInteraction = appStore.state.interactions.find(
        (ii) => ii.type === 'PostRFI'
      );
      let postRFIMarkupComp: Markup;

      if (postRFIInteraction) {
        const postRFI = postRFIInteraction as PostRFI;

        if (postRFI) {
          const { markup, rfi } = postRFI;

          if (markup !== null && rfi) {
            const markupEntity = world.find(
              (entity) =>
                entity.has(Markup) &&
                entity.get(Markup).markup.markupid === markup.markupid
            );

            if (markupEntity) {
              postRFIMarkupComp = markupEntity.get(Markup);
            }
          }
        }
      }

      world.grabAll(Markup).forEach(({ component }) => {
        const { rfis, punches } = appStore.state;

        const isHitMarkup = hitMarkups.some(
          (mc: Markup) => mc.markup.markupid === component.markup.markupid
        );
        const isHitCp = hitCps.some(
          (mc: Markup) => mc.markup.markupid === component.markup.markupid
        );
        const isPostRFIMarkup =
          postRFIMarkupComp?.markup?.markupid === component.markup.markupid;

        if (isHitMarkup && !isPostRFIMarkup) {
          const punch = punches.find((p) =>
            p.markupids.contains(component.markup.markupid)
          );

          const rfi = rfis.find((r) =>
            r.postedmarkupids.contains(component.markup.markupid)
          );

          if (punch || rfi) {
            const interaction = punch
              ? new UpdatePunch({
                  viewerOverlay: true,
                  punch,
                  fileid: component.markup.fileid,
                  containerStyles: {
                    height: `calc(100vh - 96px)`,
                    bottom: '48px',
                  },
                })
              : new ViewRFI({
                  rfi,
                  markup: component.markup,
                  containerStyles: {
                    height: 'calc(100vh - 96px)',
                    bottom: '48px',
                  },
                  placing: true,
                });

            appStore.actions.clearInteractions();
            appStore.actions.createInteraction(interaction);
          }
        }
        component.selected = isHitMarkup || isHitCp || isPostRFIMarkup;
        appStore.actions.setLocalMarkup(component.markup);
      });

      const { interactions } = appStore.state;
      const punchInteraction = interactions.find((interaction) =>
        ['CreatePunch', 'UpdatePunch'].includes(interaction.type)
      );

      if (
        hitMarkups.length === 0 &&
        hitCps.length === 0 &&
        !punchInteraction &&
        !postRFIInteraction
      ) {
        appStore.actions.clearInteractions();
      }
    });

    sheetEvents.on('drawrect:mousedown', noop);
    sheetEvents.on('drawline:mousedown', noop);
    sheetEvents.on('drawarrow:mousedown', noop);
    sheetEvents.on('drawtext:mousedown', noop);
    sheetEvents.on('drawtext:mouseup', noop);

    type MarkupEvent = {
      markupComp: Markup;
    };
    const createMarkupComponent = ({ markupComp: mc }: MarkupEvent) => {
      const dateString = new Date().toISOString();
      mc.mkMarkup?.setCreationDate(dateString);
      mc.mkMarkup?.setModDate(dateString);

      const markup = new MarkupRecord({
        markupid: uuidv4(),
        fileid: pageid,
        projectid: appStore.state.selectedProject,
        jsonBlob: JSON.stringify(mc.mkMarkup?.getAsObject()),
      });

      appStore.actions.createMarkup(markup);
    };

    sheetEvents.on('drawrect:mouseup', createMarkupComponent);
    sheetEvents.on('drawline:mouseup', createMarkupComponent);
    sheetEvents.on('drawarrow:mouseup', createMarkupComponent);

    sheetEvents.on(
      'updatetext:mode-create:onblur',
      ({ markupComp: mc }: MarkupEvent) => {
        const dateString = new Date().toISOString();
        mc.mkMarkup?.setCreationDate(dateString);
        mc.mkMarkup?.setModDate(dateString);

        const markup = new MarkupRecord({
          markupid: uuidv4(),
          fileid: pageid,
          projectid: appStore.state.selectedProject,
          jsonBlob: JSON.stringify(mc.mkMarkup?.getAsObject()),
        });
        mc.markup = markup; // Should this assignment also occur within `createMarkupComponent`  above?

        appStore.actions.createMarkup(markup);
      }
    );

    sheetEvents.on(
      'updatetext:mode-update:onblur',
      ({ markupComp: mc }: MarkupEvent) => {
        const dateString = new Date().toISOString();
        mc.mkMarkup?.setModDate(dateString);

        const updatedMarkup = mc.markup.set(
          'jsonBlob',
          JSON.stringify(mc.mkMarkup?.getAsObject())
        );

        appStore.actions.updateMarkup(updatedMarkup);
      }
    );

    sheetEvents.on('create:punch', ({ markupComp: mc }: MarkupEvent) => {
      // deselect all currently selected markups.
      world.grabAll(Markup).forEach(({ component }) => {
        component.selected = false;
      });

      const newMarkup = new NewMarkup({
        clientGeneratedID: uuidv4(),
        fileid: pageid,
        projectid: appStore.state.selectedProject,
        jsonBlob: JSON.stringify(mc.mkMarkup?.getAsObject()),
      });

      tool.tool = EditorTools.Pointer; // TEMP hack to reset for testing

      // get center of markup.
      const [b, l, t, r] = mc.mkMarkup?.getRect() as number[];
      const cx = b + (t - b) / 2;
      const cy = l + (r - l) / 2;

      // See if the placed markup is inside of a space.
      const space = appStore.state.spaces
        .get(pageid, Immutable.OrderedMap<SpaceId, SpaceRecord>())
        .valueSeq()
        .find((s) => s.pointInSpace(cx, cy));

      const createPunchInteraction = new CreatePunch({
        viewerOverlay: true,
        punch: new NewPunch({
          location: space ? space.title : '',
          projectid: appStore.state.selectedProject,
          clientcreateddate: Math.floor(Date.now() / 1000),
          markups: Immutable.List([newMarkup]),
        }),
      });

      appStore.actions.setLocalMarkup(newMarkup.intoMarkup());
      appStore.actions.createInteraction(createPunchInteraction);
    });

    sheetEvents.on(
      'create:rfi-post',
      async ({ markupComp: mc }: MarkupEvent) => {
        // an RFIPost interaction should exist
        const i = appStore.state.interactions.find(
          (ii) => ii.type === 'PostRFI'
        );
        if (!i) return;

        const postRFI = i as PostRFI;

        const markup = new MarkupRecord({
          markupid: uuidv4(),
          fileid: pageid,
          projectid: appStore.state.selectedProject,
          jsonBlob: mc.isOpen
            ? mc.markup.jsonBlob
            : JSON.stringify(mc.mkMarkup?.getAsObject()),
        });
        mc.markup = markup;
        mc.selected = true;

        const interactionWithMarkup = postRFI.set('markup', mc.markup);

        await appStore.actions.setLocalMarkup(mc.markup);

        await appStore.actions.updateInteraction(interactionWithMarkup);

        world.grabAll(Markup).forEach(({ component }) => {
          if (component.markup.markupid === mc.markup.markupid) {
            component.selected = true;
          }
        });
      }
    );

    sheetEvents.on('move:markup:mousedown', async ({ markupComp: mc, hit }) => {
      const [left, bottom] = mc.mkMarkup?.getRect();
      const { x, y } = hit || { x: 0, y: 0 };
      const isAllowed = hasAllowedRole();

      const addMarkupInteraction = () =>
        world
          .createEntity()
          .add(mc)
          .add(
            new MoveMarkupInteraction({
              originX: x,
              originY: y,
              currentX: x,
              currentY: y,
              deltaX: x - left,
              deltaY: y - bottom,
            })
          );

      if (mc.isRFI) {
        const postRFIInteraction = appStore.state.interactions.find(
          (i) => i.type === 'PostRFI'
        );
        if (isAllowed && postRFIInteraction) {
          addMarkupInteraction();
        }
      } else if (mc.isPunch) {
        if (mc.selected && isAllowed) {
          addMarkupInteraction();
        }
      } else {
        addMarkupInteraction();
      }
    });

    sheetEvents.on(
      'move:markup:mouseup',
      async ({ markupComp: mc }: MarkupEvent) => {
        const { markup, mkMarkup } = mc;

        if (mkMarkup) {
          mkMarkup.setModDate(new Date().toISOString());
          const jsonBlob = JSON.stringify(mkMarkup.getAsObject());
          const nextMarkup = markup.set('jsonBlob', jsonBlob);

          if (mc.isNewPunch) {
            if (!hasAllowedRole()) {
              return; // make sure the local markup is not changed.
            }

            actions.setLocalMarkup(nextMarkup);
            appStore.state.interactions.forEach((i) => {
              if (i.type === 'CreatePunch') {
                const nextInteraction = (i as CreatePunch).updateIn(
                  ['punch', 'markups'],
                  (markups) => {
                    return markups.map(() => {
                      return nextMarkup.intoNewMarkup();
                    });
                  }
                );
                actions.updateInteraction(nextInteraction);
              }
            });
          }
          actions.updateMarkup(nextMarkup);
        }
      }
    );

    sheetEvents.on(
      'move:controlpoint:mouseup',
      async ({ markupComp: mc }: MarkupEvent) => {
        // Save updated markup in API.
        const { markup, mkMarkup } = mc;
        // NOTE: Might be good to do some checking to see if the markup changed,
        //       And I bet there is a better way to get the jsonBlob string from a mkMarkup.
        mkMarkup?.setModDate(new Date().toISOString());
        const jsonBlob = JSON.stringify(mkMarkup?.getAsObject());
        const nextMarkup = markup.set('jsonBlob', jsonBlob);

        actions.updateMarkup(nextMarkup);
      }
    );

    const sheetEventsEntity = world
      .createEntity()
      .add(sheetComp)
      .add(sheetEvents);

    return () => {
      carnSheetEventsEntity.destroy();
      cursorEntity.destroy();
      sheetEventsEntity.destroy();
    };
  }, []);

  return null;
};

export default ECSSheetEvents;
